import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import cursor from "../../assets/images/cursor2.png";
import logger from "../../utils/logger";
import "./Header.css";

// Defines the total duration for one full animation cycle.
const CURSOR_ANIMATION_DURATION = 5.0; // Total duration in seconds

const CursorAnimation = ({
  direction,
  containerRef,
  sendEvent,
  isPaused = false,
}) => {
  logger.log(
    "🖱️ CursorAnimation rendered with direction:",
    direction,
    "isPaused:",
    isPaused
  );

  const [animationPath, setAnimationPath] = useState({ x: [0, 0], y: [0, 0] });
  const [isPathReady, setIsPathReady] = useState(false);

  // Refs are used for state that needs to be reset on each animation loop.
  // This avoids stale state within the onUpdate and onRepeat callbacks.
  const hasTriggeredSwipe = useRef(false);
  const hasTriggeredHalfCircle = useRef(false);
  const animationStartTime = useRef(null);
  const animationCycleCount = useRef(0);

  // Recalculates the animation path when the container or direction changes.
  useEffect(() => {
    logger.log("🖱️ CursorAnimation useEffect triggered, direction:", direction);

    if (!containerRef?.current) {
      logger.log("⚠️ CursorAnimation: containerRef not available");
      return;
    }

    const calculateAnimationPath = () => {
      const containerRect = containerRef.current.getBoundingClientRect();
      logger.log("📐 Container dimensions:", {
        width: containerRect.width,
        height: containerRect.height,
        direction,
      });

      // Guard against container not being ready, which can cause path issues.
      if (containerRect.width === 0 || containerRect.height === 0) {
        logger.log("⚠️ Container not ready, skipping path calculation");
        return;
      }

      // Find the actual Card element within the container to get its position
      // The Card is nested inside: container > Box wrapper > Card
      // Try multiple selectors to find the Material-UI Card component
      let cardElement =
        containerRef.current.querySelector('[class*="MuiCard-root"]') ||
        containerRef.current.querySelector('[class*="MuiPaper-root"]') ||
        containerRef.current.querySelector(".MuiCard-root") ||
        containerRef.current.querySelector(".MuiPaper-root");

      // If Material-UI selectors didn't work, try finding by style attributes
      if (!cardElement) {
        const allDivs = containerRef.current.querySelectorAll("div");
        cardElement = Array.from(allDivs).find((div) => {
          const style = window.getComputedStyle(div);
          const rect = div.getBoundingClientRect();
          // Look for a div that has border-radius and box-shadow (Card characteristics)
          // and is reasonably sized (not the container, not too small)
          return (
            (style.borderRadius.includes("px") ||
              style.borderRadius.includes("rem")) &&
            style.boxShadow !== "none" &&
            rect.width > containerRect.width * 0.4 &&
            rect.width < containerRect.width * 0.99 &&
            rect.height > containerRect.height * 0.4 &&
            rect.width > 150 // Minimum reasonable card width
          );
        });
      }

      // Final fallback: find the largest inner div that's not the container itself
      if (!cardElement) {
        const allDivs = containerRef.current.querySelectorAll("div");
        cardElement = Array.from(allDivs)
          .filter((div) => {
            const rect = div.getBoundingClientRect();
            return (
              rect.width > containerRect.width * 0.6 &&
              rect.width < containerRect.width * 0.98 &&
              rect.height > containerRect.height * 0.6 &&
              div !== containerRef.current
            );
          })
          .sort((a, b) => {
            const aRect = a.getBoundingClientRect();
            const bRect = b.getBoundingClientRect();
            return bRect.width * bRect.height - aRect.width * aRect.height;
          })[0]; // Get the largest one
      }

      let cardRect = null;
      if (cardElement) {
        cardRect = cardElement.getBoundingClientRect();
        logger.log("📐 Card dimensions found:", {
          width: cardRect.width,
          height: cardRect.height,
          left: cardRect.left,
          right: cardRect.right,
          containerLeft: containerRect.left,
          containerWidth: containerRect.width,
          containerCenter: containerRect.width / 2,
        });
      } else {
        logger.log("⚠️ Card element not found in container");
      }

      // If we found the card, use its position; otherwise fall back to container
      const paddingX = 0;
      const paddingY = 50;
      let targetX, targetY;

      // Check if mobile (screen width <= 600px)
      const isMobile = window.innerWidth <= 600;

      // Get cursor width based on screen size (matches CSS media queries)
      // Desktop: 64px, <=1200px: 38px, <=768px: 34px
      // TODO: Currently Hardcoded for these specific sizes, so change this to a more dynamic approach eventually.
      // Matches CSS widths for proper clamping/positioning
      const screenWidth = window.innerWidth;
      const cursorWidth =
        screenWidth > 1200
          ? 44
          : screenWidth > 768
            ? 20
            : 34;
      const cursorHalfWidth = cursorWidth / 2;

      // Calculate target position relative to container center
      // The cursor is positioned at container center (50%, 50%) with transform translate(-50%, -50%)
      // So coordinates are relative to the container center point (0,0 is the center)
      if (cardRect) {
        // Half-circle is 80px wide on desktop (center at 40px), 50px on mobile (center at 25px)
        // On mobile, move closer to the edge (reduce offset)
        const halfCircleCenterOffset = isMobile ? 25 : -50;

        // Calculate positions relative to container's left edge
        const containerLeft = containerRect.left;
        const containerCenterX = containerRect.width / 2;

        if (direction === "left") {
          // Card left edge relative to container left
          const cardLeftAbsolute = cardRect.left - containerLeft;
          // Target: card left edge + half-circle center - container center
          // This positions the cursor center at the half-circle center
          targetX =
            cardLeftAbsolute + halfCircleCenterOffset - containerCenterX;
          logger.log("🎯 Left target calculation:", {
            cardLeftAbsolute,
            halfCircleCenterOffset,
            containerCenterX,
            targetXBeforeOffset: targetX,
            isMobile,
          });
        } else {
          // Card right edge relative to container left
          const cardRightAbsolute = cardRect.right - containerLeft;
          // Target: card right edge - half-circle center - container center
          // This positions the cursor center at the half-circle center
          targetX =
            cardRightAbsolute - halfCircleCenterOffset - containerCenterX;
          logger.log("🎯 Right target calculation:", {
            cardRightAbsolute,
            halfCircleCenterOffset,
            containerCenterX,
            targetXBeforeOffset: targetX,
            isMobile,
          });
        }
        targetY = 0;

        // Apply the cursor image visual offset (accounts for the pointing finger part of cursor)
        // The cursor image has a pointing finger that extends, so we need to adjust
        // For left: we want the cursor tip to be at the half-circle center, so move left a bit
        // For right: we want the cursor tip to be at the half-circle center, so move right a bit
        // Since we're using negative values (left of center), for right we need less negative
        // On mobile, adjust offset to move cursor further to edge
        const cursorVisualOffset = isMobile
          ? direction === "left"
            ? -25
            : -15
          : direction === "left"
            ? -35
            : -110;
        targetX += cursorVisualOffset;

        const minX = -containerRect.width / 2 + cursorHalfWidth;
        const maxX = containerRect.width / 2 - cursorHalfWidth;

        targetX = Math.max(minX, Math.min(maxX, targetX));
      } else {
        // Fallback: use container edges (original behavior)
        if (direction === "left") {
          targetX = -containerRect.width / 2 + paddingX + cursorHalfWidth;
        } else {
          targetX = containerRect.width / 2 - paddingX - cursorHalfWidth;
        }
        targetY = 0;
        // Apply cursor offset for fallback case
        targetX -= 40;

        // Clamp fallback targetX as well
        const minX = -containerRect.width / 2 + cursorHalfWidth;
        const maxX = containerRect.width / 2 - cursorHalfWidth;
        targetX = Math.max(minX, Math.min(maxX, targetX));
      }

      // The animation starts from the bottom-center and moves towards the horizontal edge.
      // startX = 0 ensures the cursor starts at the horizontal center of the container
      // (since the cursor is positioned at 50% with translate(-50%, -50%), 0 is the center)
      const startX = -25;
      const startY = containerRect.height / 2 - paddingY;

      const newPath = {
        x: [startX, targetX],
        y: [startY, targetY],
      };

      setAnimationPath(newPath);
      setIsPathReady(true);
    };

    // Small delay to ensure Card is rendered
    const timeoutId = setTimeout(() => {
      calculateAnimationPath();
    }, 50);

    // Debounce resize: the path calc does several querySelectorAll("div") +
    // getComputedStyle sweeps, so running it per resize event makes dragging the
    // window lag. Only recalc once the resize settles.
    let resizeTimeoutId = null;
    const handleResize = () => {
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
      resizeTimeoutId = setTimeout(() => {
        calculateAnimationPath();
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [direction, containerRef]);

  // Reset animation state when unpausing
  useEffect(() => {
    if (!isPaused) {
      // Reset refs when resuming from pause to ensure clean animation restart
      hasTriggeredSwipe.current = false;
      hasTriggeredHalfCircle.current = false;
      animationStartTime.current = null;
    }
  }, [isPaused]);

  // Defines the multi-stage cursor animation using variants.
  const cursorVariants = {
    animate: {
      x: [
        animationPath.x[0],
        animationPath.x[1],
        animationPath.x[1],
        animationPath.x[1],
        animationPath.x[1],
      ],
      y: [
        animationPath.y[0],
        animationPath.y[1],
        animationPath.y[1],
        animationPath.y[1],
        animationPath.y[1],
      ],
      scale: [1, 1, 0.7, 1, 1],
      transition: {
        duration: CURSOR_ANIMATION_DURATION,
        repeat: isPaused ? 0 : Infinity,
        times: [0, 0.7, 0.8, 0.9, 1],
        ease: "easeInOut",
        // This function now correctly resets the refs for the next loop.
        onRepeat: () => {
          if (isPaused) return;
          animationCycleCount.current += 1;
          logger.log(
            "🔄 CursorAnimation onRepeat - Cycle",
            animationCycleCount.current,
            {
              direction,
              previousSwipeTriggered: hasTriggeredSwipe.current,
              previousHalfCircleTriggered: hasTriggeredHalfCircle.current,
            }
          );

          hasTriggeredSwipe.current = false;
          hasTriggeredHalfCircle.current = false;
          animationStartTime.current = Date.now();

          logger.log("🔄 CursorAnimation refs reset for new cycle");
        },
      },
    },
    paused: {
      x: animationPath.x[0],
      y: animationPath.y[0],
      scale: 1,
      transition: {
        duration: 0,
      },
    },
  };

  // This function is called on every frame of the animation.
  const handleAnimationUpdate = () => {
    if (isPaused) return;

    if (!animationStartTime.current) {
      animationStartTime.current = Date.now();
      return;
    }

    const currentTime = Date.now();
    const elapsedTime = (currentTime - animationStartTime.current) / 1000;
    const progress = Math.min(elapsedTime / CURSOR_ANIMATION_DURATION, 1);

    // Trigger half-circle event at 50% of animation duration (while moving)
    if (!hasTriggeredHalfCircle.current && progress >= 0.5) {
      hasTriggeredHalfCircle.current = true;
      sendEvent("show-half-circle", direction);
    }

    // Trigger the swipe event when the 'click' animation happens
    if (!hasTriggeredSwipe.current && progress >= 0.8) {
      hasTriggeredSwipe.current = true;
      sendEvent("swipe", direction);
    }
  };

  return (
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
      {isPathReady && (
        <motion.img
          src={cursor}
          alt="cursor"
          variants={cursorVariants}
          animate={isPaused ? "paused" : "animate"}
          onUpdate={handleAnimationUpdate}
          // Initialize the start time when the animation first begins.
          onAnimationStart={() => {
            if (isPaused) return;
            logger.log("🎬 CursorAnimation started:", {
              direction,
              cycle: animationCycleCount.current,
              timestamp: Date.now(),
            });
            animationStartTime.current = Date.now();
          }}
          onAnimationComplete={() => {
            if (isPaused) return;
            logger.log("🏁 CursorAnimation completed cycle:", {
              direction,
              cycle: animationCycleCount.current,
            });
          }}
          className="cursor"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </div>
  );
};

export default CursorAnimation;
